import { useEffect, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import axios from "axios"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type {
  HostedAgentSource,
  HostedAgentSourceContent,
} from "@/api/client/hosted-agents-sources"

import {
  listAdminAgentSources,
  readAdminAgentSource,
  revokeAdminAgentSource,
  saveAdminAgentSource,
} from "@/api/admin/hosted-agents-sources"
import { FileText, Plus, Trash2 } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"

import { useRegisterAdminDirty } from "../admin-unsaved-changes-context"

type Draft = {
  source?: HostedAgentSource
  file?: File
  title: string
  usage_context: string
  requestKey: string
}

/** Own knowledge is saved independently, with a version check for every change. */
export function HostedAgentSourcesTab({
  agentId,
  onDirtyChange,
  onBusyChange,
}: {
  agentId: string
  onDirtyChange?: (dirty: boolean) => void
  onBusyChange?: (busy: boolean) => void
}) {
  const { t } = useTranslation()
  const text = (key: string) => t(`adminDashboard.hostedAgents.sources.${key}`)
  const query = useQuery({
    queryKey: ["admin", "hosted-agents", agentId, "sources"],
    queryFn: () => listAdminAgentSources(agentId),
    staleTime: 0,
  })
  const [draft, setDraft] = useState<Draft | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  const [removing, setRemoving] = useState<HostedAgentSource | null>(null)
  const [preview, setPreview] = useState<HostedAgentSourceContent | null>(null)
  useRegisterAdminDirty(`agent-source:${agentId}`, Boolean(draft))
  useEffect(() => {
    onDirtyChange?.(Boolean(draft))
    return () => onDirtyChange?.(false)
  }, [draft, onDirtyChange])
  useEffect(() => {
    onBusyChange?.(busy)
    return () => onBusyChange?.(false)
  }, [busy, onBusyChange])
  const capabilities = query.data?.capabilities
  useEffect(() => {
    if (
      preview &&
      query.data &&
      !query.data.sources.some((source) => source.id === preview.source.id)
    )
      setPreview(null)
  }, [preview, query.data])
  const fail = (cause: unknown) => {
    const code = axios.isAxiosError(cause) ? cause.response?.data?.code : undefined
    const known = [
      "source_version_conflict",
      "source_type_unsupported",
      "source_too_large",
      "source_invalid",
      "source_parse_failed",
      "source_parser_busy",
      "source_not_found",
      "source_limit_reached",
      "source_version_limit",
      "source_lifecycle_limit",
      "source_unavailable",
      "source_cleanup_pending",
    ]
    setError(text(known.includes(code) ? `errors.${code}` : "errors.unknown"))
    if (
      code === "source_version_conflict" ||
      code === "source_not_found" ||
      code === "source_cleanup_pending"
    ) {
      setPreview(null)
      void query.refetch()
    }
  }
  const edit = (source?: HostedAgentSource) => {
    setError("")
    setPreview(null)
    setDraft({
      source,
      title: source?.title ?? "",
      usage_context: source?.usage_context ?? "",
      requestKey: crypto.randomUUID(),
    })
  }
  const changeDraft = (values: Partial<Draft>) =>
    setDraft((current) =>
      current ? { ...current, ...values, requestKey: crypto.randomUUID() } : current
    )
  const save = async () => {
    if (!draft || busy) return
    setBusy(true)
    setError("")
    try {
      await saveAdminAgentSource(agentId, draft)
      setDraft(null)
      await query.refetch()
      toast.success(text("saved"))
    } catch (cause) {
      fail(cause)
    } finally {
      setBusy(false)
    }
  }
  const read = async (source: HostedAgentSource, offset = 0) => {
    setBusy(true)
    setError("")
    try {
      setPreview(await readAdminAgentSource(agentId, source, offset))
    } catch (cause) {
      setPreview(null)
      fail(cause)
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">{text("help")}</p>
      <p className="text-muted-foreground text-xs">{text("separateSave")}</p>
      {query.isLoading && (
        <p className="flex items-center gap-2 text-sm" role="status">
          <Spinner />
          {text("loading")}
        </p>
      )}
      {query.isError && (
        <div className="space-y-2 text-sm" role="alert">
          <p>{text("errors.source_unavailable")}</p>
          <Button variant="outline" onClick={() => void query.refetch()}>
            {text("retry")}
          </Button>
        </div>
      )}
      {capabilities && (
        <p className="bg-muted/30 rounded-md border p-3 text-xs">
          {t("adminDashboard.hostedAgents.sources.capabilities", {
            types: capabilities.accepted_extensions.join(", "),
            size: Math.floor(capabilities.max_file_bytes / 1024 / 1024),
            count: capabilities.max_sources,
          })}{" "}
          {!capabilities.images && text("unsupported")}
        </p>
      )}
      {error && (
        <p
          className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-sm"
          role="alert"
        >
          {error}
        </p>
      )}
      {!draft && capabilities && (
        <Button
          disabled={busy || (query.data?.sources.length ?? 0) >= capabilities.max_sources}
          type="button"
          variant="outline"
          onClick={() => edit()}
        >
          <Plus className="size-4" />
          {text("add")}
        </Button>
      )}
      {draft && (
        <div className="space-y-3 rounded-md border p-4">
          <h3 className="text-sm font-semibold">{text(draft.source ? "edit" : "add")}</h3>
          <div className="space-y-2">
            <Label htmlFor="agent-source-file">{text(draft.source ? "replacement" : "file")}</Label>
            <Input
              accept={capabilities?.accepted_extensions.join(",")}
              disabled={busy}
              id="agent-source-file"
              type="file"
              onChange={(event) => {
                const file = event.target.files?.[0]
                if (file && capabilities && file.size > capabilities.max_file_bytes) {
                  setError(text("errors.source_too_large"))
                  event.target.value = ""
                  changeDraft({ file: undefined })
                  return
                }
                setError("")
                changeDraft({ file, title: draft.title || file?.name.slice(0, 180) || "" })
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="agent-source-title">{text("title")}</Label>
            <Input
              disabled={busy}
              id="agent-source-title"
              maxLength={180}
              value={draft.title}
              onChange={(event) => changeDraft({ title: event.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="agent-source-context">{text("context")}</Label>
            <Textarea
              disabled={busy}
              id="agent-source-context"
              maxLength={500}
              placeholder={text("contextExample")}
              rows={3}
              value={draft.usage_context}
              onChange={(event) => changeDraft({ usage_context: event.target.value })}
            />
            <p className="text-muted-foreground text-xs">{text("contextHelp")}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={busy || (!draft.file && !draft.source)}
              type="button"
              onClick={() => void save()}
            >
              {busy && <Spinner />}
              {text(busy ? "processing" : "save")}
            </Button>
            <Button
              disabled={busy}
              type="button"
              variant="outline"
              onClick={() => {
                setDraft(null)
                setError("")
              }}
            >
              {text("cancel")}
            </Button>
          </div>
        </div>
      )}
      {query.data?.sources.length === 0 && !draft && (
        <div className="rounded-md border p-5 text-sm">
          <FileText className="text-muted-foreground mb-2 size-6" />
          <p>{text("empty")}</p>
        </div>
      )}
      <ul className="space-y-2">
        {query.data?.sources.map((source) => (
          <li key={source.id} className="space-y-2 rounded-md border p-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium break-words">{source.title}</p>
                <p className="text-muted-foreground text-xs break-all">
                  {source.filename} · v{source.version} · {text(source.status)}
                </p>
              </div>
              <div className="flex flex-wrap gap-1">
                <Button
                  disabled={busy || Boolean(draft)}
                  size="sm"
                  type="button"
                  variant="outline"
                  onClick={() => void read(source)}
                >
                  {text("read")}
                </Button>
                <Button
                  disabled={busy || Boolean(draft)}
                  size="sm"
                  type="button"
                  variant="ghost"
                  onClick={() => edit(source)}
                >
                  {text("edit")}
                </Button>
                <Button
                  aria-label={`${text("remove")} ${source.title}`}
                  disabled={busy || Boolean(draft)}
                  size="icon"
                  type="button"
                  variant="ghost"
                  onClick={() => setRemoving(source)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </div>
            {source.usage_context && (
              <p className="text-muted-foreground text-xs break-words whitespace-pre-wrap">
                {source.usage_context}
              </p>
            )}
          </li>
        ))}
      </ul>
      {preview && (
        <section aria-label={preview.source.title} className="space-y-3 rounded-md border p-4">
          <div className="flex items-start justify-between gap-2">
            <h4 className="text-sm font-medium">
              {preview.source.title} · v{preview.source.version}
            </h4>
            <Button size="sm" variant="ghost" onClick={() => setPreview(null)}>
              {text("close")}
            </Button>
          </div>
          <pre className="max-h-72 overflow-auto font-sans text-sm break-words whitespace-pre-wrap">
            {preview.content}
          </pre>
          {preview.next_offset !== null && (
            <Button
              disabled={busy}
              variant="outline"
              onClick={() => void read(preview.source, preview.next_offset!)}
            >
              {text("nextPage")}
            </Button>
          )}
        </section>
      )}
      <AlertDialog
        open={Boolean(removing)}
        onOpenChange={(open) => {
          if (!open && !busy) setRemoving(null)
        }}
      >
        <AlertDialogContent>
          {error && (
            <p
              className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-sm"
              role="alert"
            >
              {error}
            </p>
          )}
          <AlertDialogHeader>
            <AlertDialogTitle>{text("removeTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{text("removeHelp")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>{text("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={(event) => {
                event.preventDefault()
                if (!removing) return
                setBusy(true)
                setError("")
                void revokeAdminAgentSource(agentId, removing)
                  .then(async () => {
                    setRemoving(null)
                    setPreview(null)
                    await query.refetch()
                    toast.success(text("removed"))
                  })
                  .catch(fail)
                  .finally(() => setBusy(false))
              }}
            >
              {text("remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
